import React, { useState, useRef } from "react";
import "./public/styles/styles.css";
import { MailingListCTA, MailingListCTARef } from "./components/MailingListCTA";
import { BuyMeCoffeeCTA } from "./components/BuyMeCoffeeCTA";
import { trackEvent } from "./lib/analytics";

type MenuBarSection = {
  label: string | React.ReactNode;
  items: {
    label: string | React.ReactNode;
    /** Reported to analytics in place of a label that is not plain text. */
    name?: string;
    onClick: () => void;
  }[];
};

export const defaultMenuBarSections: MenuBarSection[] = [
  {
    label: "FILE",
    items: [
      {
        label: "SHARE",
        onClick: () => {
          console.log("SAVE");
        },
      },
    ],
  },
  {
    label: "VIEW",
    items: [
      {
        label: "ROWS",
        onClick: () => {
          console.log("TIMELINE");
        },
      },
      {
        label: "SHOW SIDEBAR",
        onClick: () => {
          console.log("3D GRAPH");
        },
      },
    ],
  },
];

export const defaultSideBarSections = [
  {
    title: "DETAILS",
    // collapseState: useState(false),
    body: (
      <>
        <div className="flex flex-row items-center p-1">
          <i className="fas fa-info-circle"></i> Coming Soon
        </div>
      </>
    ),
  },
];

const hamburgerMenu = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-menu"
  >
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

/** A menu label as plain text, for analytics; a non-text label needs a `name`. */
const menuText = (label: string | React.ReactNode) =>
  typeof label === "string" ? label : "";

export interface LayoutRef {
  expandSidebar: () => void;
}

const Layout = React.forwardRef<
  LayoutRef,
  {
    menuBarSections: MenuBarSection[];
    sideBarSections: {
      title: string;
      body: React.ReactNode;
    }[];
    children: React.ReactNode;
    isDarkMode: boolean;
    title: string;
    showSidebar: boolean;
    mailingListRef?: React.RefObject<MailingListCTARef>;
  }
>(
  (
    {
      menuBarSections = defaultMenuBarSections,
      sideBarSections = defaultSideBarSections,
      isDarkMode = false,
      children,
      title,
      showSidebar = false /* TODO: support more details in sidebar */,
      mailingListRef,
    },
    ref,
  ) => {
    const [isMenuBarOpen, setIsMenuBarOpen] = useState(false);
    const [isSidebarMinimized, setIsSidebarMinimized] = useState(true);

    React.useImperativeHandle(ref, () => ({
      expandSidebar: () => setIsSidebarMinimized(false),
    }));

  const isSmallScreen = window.innerWidth < 768;
  const [sectionCollapsedStates, setSectionCollapsedStates] = useState<{
    [key: string]: boolean;
  }>({
    // Key is Title + index
    ...sideBarSections.reduce(
      (acc, section, i) => ({ ...acc, [section.title + i]: !isSmallScreen }),
      {},
    ),
    DETAILS0: !isSmallScreen,
    PATH1: !isSmallScreen,
    COMMENTS2: !isSmallScreen,
  });

  const logoSrc =
    "https://surulere.dev/assets/img/suruleredotdev_transparent_bg.svg";

  return (
    <div
      id="layout"
      className={`${isDarkMode ? "DARK" : "LIGHT"} w-full h-full flex flex-col`}
      style={{ minHeight: "100vh" }}
    >
      <div
        id="header"
        className="flex flex-row justify-between items-center p-3 pb-1 sticky top-0 z-50"
      >
        <div id="title" className="flex flex-row items-center p-1">
          <a
            href="https://surulere.dev"
            target="_blank"
            className="hover:opacity-75 cursor-pointer"
          >
            <img
              src={logoSrc}
              alt="Surulere.dev"
              className="p-1"
              style={{ width: "var(--bg-size)" }}
            />
          </a>
          <div className="text-2xl font-bold border border-theme-color bg-theme-color p-1 px-2 ml-2">
            {title}
          </div>
        </div>
        <div
          id="menu-bar"
          className="flex md:flex-row flex-col items-center border border-theme-color"
        >
          <div
            className="menu-bar-section-toggle bg-theme-color md:hidden"
            onClick={() => setIsMenuBarOpen(!isMenuBarOpen)}
          >
            <div className="menu-bar-section-toggle-icon p-1">
              {hamburgerMenu}
            </div>
          </div>
          <div
            className={`menu-bar-sections z-20 ${
              isMenuBarOpen
                ? "absolute top-10 right-10 flex flex-col md:flex-row"
                : "hidden md:flex flex-row"
            } bg-theme-color p-1 md:border-none border border-theme-color md:w-auto w-1/2`}
          >
            {/*  TODO: get divide working, w screen size: divide-y md:divide-x divide-dashed divide-black */}
            {menuBarSections.map((section, i) => (
              <div
                key={i}
                className={`menu-bar-section flex md:flex-row flex-col px-1 ${
                  i === menuBarSections.length - 1
                    ? ""
                    : "border-b-2 md:border-b-0 md:border-r-2 border-dashed border-theme-color"
                }`}
              >
                <div className="menu-bar-section-label font-bold p-1">
                  {section.label}
                </div>
                {section.items.map((item, j) => (
                  <div
                    key={j}
                    className="menu-bar-section-item p-1 hover:opacity-50 cursor-pointer"
                    onClick={() => {
                      trackEvent("menu_click", {
                        menu_item: `${menuText(section.label)} > ${
                          item.name ?? menuText(item.label)
                        }`,
                      });
                      item.onClick();
                    }}
                  >
                    {item.label}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div id="content" className="flex md:flex-row md:p-3 p-1 md:pt-0 pt-0">
        <div id="main" className={`"md:w-full" w-full h-full`}>
          <div id="main-content" className="ph-1 mr-2 w-full h-full">
            {children}
          </div>
        </div>

        {showSidebar && (
          <div
            id="sidebar"
            className={`z-10 fixed floater-bottom-row floater-right transition-all duration-300 ease-in-out
              ${
                isSidebarMinimized
                  ? ""
                  : "md:w-1/4 w-[calc(100%-1rem)] md:max-w-sm max-h-[85vh] md:max-h-[40vh]"
              }`}
            style={isSidebarMinimized ? {} : { maxWidth: "400px" }}
          >
            {isSidebarMinimized ? (
              <button
                onClick={() => {
                  // Scroll to the top and focus the #keyword-search-hero-input element
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  setTimeout(() => {
                    const el = document.getElementById("keyword-search-hero-input");
                    if (el && typeof el.focus === "function") {
                      el.focus();
                    }
                  }, 400); // Allow scroll animation to complete
                }}
                className="bg-theme-color border border-theme-color p-2 px-3 hover:opacity-75 flex items-center gap-2"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                <span className="text-sm hidden md:inline font-bold">
                  SEARCH & FILTER
                </span>
              </button>
            ) : (
              <div className="bg-theme-color border border-theme-color overflow-y-auto max-h-[inherit]">
                <div className="flex items-center justify-between p-2 border-b-2 border-dashed border-theme-color sticky top-0 bg-theme-color z-10">
                  <span className="font-bold px-2">SEARCH & FILTER</span>
                  <button
                    onClick={() => setIsSidebarMinimized(true)}
                    className="p-1 hover:opacity-50"
                    title="Minimize"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14" />
                    </svg>
                  </button>
                </div>
                <div id="sidebar-content" className="pr-2 w-full">
                  {sideBarSections.map((section, i) => (
                    <div key={i} className="sidebar-section">
                      {sideBarSections.length > 1 && (
                        <div className="sidebar-section-title flex flex-row items-center p-1 sticky top-10 bg-theme-color">
                          <div className="p-1 px-2 ml-2 flex flex-row items-center justify-between w-full border-b-2 border-dashed border-theme-color">
                            <span className="sidebar-section-title-text font-bold">
                              {section.title}
                            </span>
                            <div
                              className="sidebar-section-title-toggle cursor-pointer"
                              onClick={() =>
                                setSectionCollapsedStates((prev) => ({
                                  ...prev,
                                  [section.title + i]: !prev[section.title + i],
                                }))
                              }
                            >
                              {sectionCollapsedStates[section.title + i] ? (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="lucide lucide-chevron-down"
                                >
                                  <path d="M6 9l6 6 6-6"></path>
                                </svg>
                              ) : (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="lucide lucide-chevron-right"
                                >
                                  <path d="M9 18l6-6-6-6"></path>
                                </svg>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      <div
                        className={`sidebar-section-body transition-all duration-300 ease-in-out ${
                          sideBarSections.length === 1 ||
                          sectionCollapsedStates[section.title + i]
                            ? "max-h-screen opacity-100"
                            : "max-h-0 opacity-0 overflow-hidden"
                        }`}
                      >
                        {section.body}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="fixed z-40 floater-left floater-bottom-row flex flex-row items-end gap-0 max-w-md">
        <MailingListCTA ref={mailingListRef} />
        <BuyMeCoffeeCTA />
      </div>

      <div
        id="copyright-footer"
        className="fixed floater-bottom floater-right p-2 pb-0.5 text-sm z-30"
      >
        <a
          href="https://surulere.dev"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="text-theme-color">
            &copy; SURULERE.DEV {new Date().getFullYear()}
          </span>
        </a>
      </div>
    </div>
  );
  },
);

export default Layout;

export const ModalLayout: React.FC<{
  title: string | React.ReactNode;
  children: React.ReactNode;
  onClose: () => void;
  setToast?: (toast: {
    type: string;
    message: string;
    timeout?: number;
  }) => void;
}> = ({ title, children, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-20 text-theme-color">
      <div className="bg-white/80 p-3 shadow-md w-full max-w-6xl max-h-full overflow-y-auto">
        {/* Header */}
        <h2 id="modal-title" className="text-xl mb-4">
          {title}
        </h2>

        {children}

        <div className="flex justify-end mt-4 gap-2 sticky bottom-0 z-10 p-2">
          <button
            onClick={onClose}
            className={`text-theme-color border border-theme-color p-1 px-2 bg-theme-color`}
          >
            CLOSE
          </button>
        </div>
      </div>
    </div>
  );
};

export const TabLayout: React.FC<{
  tabs: { label: string; onClick: () => void; body: React.ReactNode }[];
}> = ({ tabs }) => {
  const [activeTab, setActiveTab] = useState(tabs[0].label);
  return (
    <div className="tab-layout">
      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 pt-2">
        <div className="border-b border-theme-color">
          <nav className="-mb-px flex space-x-4">
            {tabs.map((tab, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(tab.label)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.label
                    ? "border-theme-color"
                    : "border-transparent hover:border-theme-color"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 py-2">
        {tabs.map((tab, index) => (
          <div
            key={index}
            style={{ display: activeTab === tab.label ? "block" : "none" }}
          >
            {tab.body}
          </div>
        ))}
      </div>
    </div>
  );
};
